import { PageOptions } from '@graphcommerce/framer-next-pages'
import { ProductListDocument, ProductListQuery } from '@graphcommerce/magento-product'
import { StoreConfigDocument } from '@graphcommerce/magento-store'
import { GetStaticProps, LayoutHeader, MetaRobots, PageMeta } from '@graphcommerce/next-ui'
import { LayoutNavigation, LayoutNavigationProps, RowProduct, RowRenderer } from '../components'
import { LayoutDocument } from '../components/Layout/Layout.gql'
import { DefaultPageDocument, DefaultPageQuery } from '../graphql/DefaultPage.gql'
import { graphqlSharedClient, graphqlSsrClient } from '../lib/graphql/graphqlSsrClient'
import { GoogleReviews, GoogleReviewType} from '../components/GoogleReviews'
import { RowQuote } from '../components/GraphCMS/RowQuote/RowQuote'
import getReviews from "../lib/google-places/google-places";
import {RowLinks} from '../components/GraphCMS/RowLinks/RowLinks'
import {CategoryPageDocument, CategoryPageQuery} from '../graphql/CategoryPage.gql'
import {BrandsRowLinks} from '../components/BrandsRowLinks/BrandsRowLinks'

type Props = DefaultPageQuery & {
  latestList: ProductListQuery
  specialOffersList: ProductListQuery
  reviews: Array<GoogleReviewType>,
  brandsCategory: CategoryPageQuery
}
type RouteProps = { url: string }
type GetPageStaticProps = GetStaticProps<LayoutNavigationProps, Props, RouteProps>

function CmsPage(props: Props) {
  const { pages, latestList, specialOffersList, reviews, brandsCategory } = props
  const page = pages?.[0]

  const latest = latestList?.products?.items?.[0]
  const favorite = specialOffersList?.products?.items?.[0]

  return (
    <>
      <PageMeta
        title={page?.metaTitle ?? page?.title ?? ''}
        metaDescription={page?.metaDescription ?? ''}
        metaRobots={page?.metaRobots.toLowerCase().split('_') as MetaRobots[] | undefined}
        canonical='/'
      />

      <LayoutHeader floatingMd floatingSm />

      {page && (
        <RowRenderer
          content={page.content}
          renderer={{
            RowProduct: (rowProps) => {
              const { identity } = rowProps

              if (identity === 'home-special-sales') {
                return (
                  <RowProduct {...rowProps} {...favorite} items={specialOffersList.products?.items} />
                )
              }

              if (identity === 'home-latest') {
                return <RowProduct {...rowProps} {...latest} items={latestList.products?.items} />
              }

              return (
                <RowProduct {...rowProps} {...favorite} items={specialOffersList.products?.items} />
              )
            },
            RowQuote: (rowProps) => {
              const { identity } = rowProps

              if (identity === 'home-reviews' && reviews.length > 0) {
                return (
                  <GoogleReviews reviews={reviews} />
                )
              }

              return <RowQuote {...rowProps} />
            },
            RowLinks: (rowProps) => {
              const { identity } = rowProps

              if (identity === 'home-brands') {
                return (
                  <BrandsRowLinks {...rowProps} brandsCategory={brandsCategory} />
                )
              }
              return <RowLinks {...rowProps} />
            }
          }}
        />
      )}
    </>
  )
}

CmsPage.pageOptions = {
  Layout: LayoutNavigation,
} as PageOptions

export default CmsPage

export const getStaticProps: GetPageStaticProps = async ({ locale }) => {
  const client = graphqlSharedClient(locale)
  const staticClient = graphqlSsrClient(locale)

  const conf = client.query({ query: StoreConfigDocument })
  const page = staticClient.query({ query: DefaultPageDocument, variables: { url: `page/home` } })
  const layout = staticClient.query({ query: LayoutDocument })

  // todo(paales): Remove when https://github.com/Urigo/graphql-mesh/issues/1257 is resolved
  const latestList = staticClient.query({
    query: ProductListDocument,
    variables: { pageSize: 8, filters: { category_uid: { eq: 'Mzg=' } }, sort: { position: 'DESC'} },
  })

  const specialOffersList = staticClient.query({
    query: ProductListDocument,
    variables: { pageSize: 8, filters: { category_uid: { eq: 'Mzk=' } }, sort: { name: 'DESC'} },
  })

  const categoryPage = staticClient.query({
    query: CategoryPageDocument,
    variables: { url: locale == 'en' ? 'brands' : 'marcas' },
  })
  const brandsCategory = await categoryPage

  const reviews = await getReviews({
    language: locale ?? import.meta.graphCommerce.storefront[0].locale,
    limit: 12,
    minimumRating: 3
  })

  if (!(await page).data.pages?.[0]) return { notFound: true }

  return {
    props: {
      ...(await page).data,
      ...(await layout).data,
      latestList: (await latestList).data,
      specialOffersList: (await specialOffersList).data,
      reviews: reviews ?? [],
      apolloState: await conf.then(() => client.cache.extract()),
      brandsCategory: brandsCategory.data,
    },
    revalidate: 60 * 60 * 24,
  }
}
